import { behaviorKeysByVertical } from '@amzn/aet-coachingform-kit/lib/behavior';
import { getValuesByAttribute } from '@amzn/aet-coachingform-kit/lib/helper';
import {
  metricKeysByTopic,
  metricKeysByVertical,
} from '@amzn/aet-coachingform-kit/lib/metric';
import {
  ActiveCoachingTopic,
  ActiveTenet,
} from '@amzn/aet-coachingform-kit/lib/types';
import { IntlShape } from 'react-intl';

import {
  BehaviorObject,
  CoachingDetailsInput,
  CoachingTopicObject,
  ExtendMetricObject,
  MetricObject,
  TenetObject,
  VerticalType,
} from '../../../api/CoachingForm/schema';
import {
  getObjectOptions,
  getObjectOptionsByKeys,
} from '../FieldComponents/Helpers';
import { Option } from '../FieldComponents/Select';
import { checkGoUser } from '../../../Util/User';
import { Metric } from './FormFields';

export const maxCoachingDetail = 3;
export const maxContactReview = 3;
const rapMetrics = [
  'HMD',
  'HMD_YES',
  'HMD_NO',
  'HMD_OFFER_RATE',
  'RAP_REPEAT',
  'RAP',
  'PRR',
  'PRR_EXTRACTION',
];
const rapRemovalVerticals = ['Consumer', 'SDSCAP'];

export const filterRAPMetric = (
  metric: string,
  contactReviewVerticals: VerticalType[] | undefined,
  requesterOwner: string,
  selectedMetric?: string | null
): boolean => {
  if (
    selectedMetric !== undefined &&
    selectedMetric !== null &&
    rapMetrics.includes(selectedMetric)
  ) {
    return true;
  }
  if (!checkGoUser(requesterOwner)) {
    if (
      contactReviewVerticals?.every(vertical =>
        rapRemovalVerticals.includes(vertical)
      )
    ) {
      return !rapMetrics.includes(metric);
    }
  }
  return true;
};

export const getAllTopics = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [CoachingTopicObject.QUALITY]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_topic_quality',
      defaultMessage: 'Contact quality',
    }),
    [CoachingTopicObject.PRODUCTIVITY]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_topic_productivity',
      defaultMessage: 'Productivity',
    }),
    [CoachingTopicObject.COMPLIANCE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_topic_compliance',
      defaultMessage: 'Compliance',
    }),
    [CoachingTopicObject.DEVELOPMENT]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_topic_development_or_other',
      defaultMessage: 'Development or other',
    }),
    [CoachingTopicObject.QCS]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_topic_qcs',
      defaultMessage: 'QCS',
    }),
  };

  return getObjectOptions(CoachingTopicObject, translations);
};

export const getTopics = (intl: IntlShape): Option[] => {
  const excludedTopicList = [CoachingTopicObject.QCS];

  return getAllTopics(intl).filter(
    topic =>
      !excludedTopicList.includes(topic.value.toString()) &&
      topic.value in ActiveCoachingTopic
  );
};

export const getTopicsByMetric = (
  intl: IntlShape,
  metric: string | null
): Option[] => {
  return getAllTopics(intl).filter(topic => {
    return getTopicByMetricList(metric!).indexOf(String(topic.value)) !== -1;
  });
};

export const getAllTenets = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [TenetObject.FRIEND]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_tenet_friend',
      defaultMessage: 'Treat every customer as a friend',
    }),
    [TenetObject.EFFORT]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_tenet_effort',
      defaultMessage: 'Reduce customer effort',
    }),
    [TenetObject.GUESS]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_tenet_answer',
      defaultMessage: 'Answer the question asked; never guess',
    }),
    [TenetObject.SOLVE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_tenet_solve',
      defaultMessage: 'Solve the problem',
    }),
    [TenetObject.ESCALATE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_tenet_escalate',
      defaultMessage: 'Escalate systemic problems',
    }),
    [TenetObject.NA]: intl.formatMessage({
      id: 'cs_perfportal_not_applicable_full',
      defaultMessage: 'Not Applicable',
    }),
  };

  return getObjectOptions(TenetObject, translations);
};

export const getTenets = (intl: IntlShape): Option[] => {
  return getAllTenets(intl).filter(tenet => tenet.value in ActiveTenet);
};

export const getAllMetrics = (): Option[] => {
  const translations: Record<string, string> = {};
  return getObjectOptionsByKeys(ExtendMetricObject(), translations);
};

// @TODO - Remove old CCX metrics and hiding logic - SIM: https://sim.amazon.com/issues/CSKoala-1229
// Hiding old CCX Metrics from the option list for backward compatible solution
export const ccxOldMetrics = [
  'FRIENDLINESS',
  'KNOWLEDGE',
  'LISTENING',
  'UNDERSTANDABILITY',
  'OVERALL_SERVICE',
  'ATTENTIVENESS',
];
export const getMetrics = (
  requesterOwner: string,
  topic?: CoachingDetailsInput['coachingTopic'],
  verticals?: VerticalType[],
  relatedMetric?: CoachingDetailsInput['relatedMetric']
): Option[] => {
  // @TODO - Remove old CCX metrics and hiding logic - SIM: https://sim.amazon.com/issues/CSKoala-1229
  // Hiding old CCX Metrics from the option list for backward compatible solution
  return (
    getAllMetrics()
      .filter(
        option =>
          topic === undefined ||
          topic === null ||
          !verticals ||
          verticals.length === 0 ||
          (getValuesByAttribute(metricKeysByTopic, [topic]).includes(
            option.value
          ) &&
            getValuesByAttribute(metricKeysByVertical, verticals).includes(
              option.value
            ))
      )
      .filter(option => !ccxOldMetrics.includes(option.value.toString()))
      .filter(option =>
        filterRAPMetric(
          option.value.toString(),
          verticals,
          requesterOwner,
          relatedMetric
        )
      )
      // @TODO - Metric Enum Cleanup - https://issues.amazon.com/issues/CSKoala-2140
      .filter(option => {
        if (
          relatedMetric !== undefined &&
          relatedMetric !== null &&
          MetricObject['FOLLOW_UP'].includes(relatedMetric)
        ) {
          return true;
        }

        return !MetricObject['FOLLOW_UP'].includes(option.value.toString());
      })
      .sort((a, b) => {
        return a.label > b.label ? 1 : a.label < b.label ? -1 : 0;
      })
  );
};

export const getTopicByMetric = (metric: string): string | undefined => {
  let topic: string | undefined = undefined;
  Object.keys(CoachingTopicObject).forEach(key => {
    if (getMetrics(key).find(m => m.value === metric)) {
      topic = key;
    }
  });
  return topic;
};

export const getTopicByMetricList = (metric: string): string[] => {
  const topic: string[] = [];
  Object.keys(CoachingTopicObject).forEach(key => {
    if (getMetrics(key).find(m => m.value === metric)) {
      topic.push(key);
    }
  });
  return topic.sort();
};

export const getAllBehaviors = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [BehaviorObject.PERSONALIZE_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_personalized',
      defaultMessage: "Personalized contact by using the customer's name",
    }),
    [BehaviorObject.PERSONALIZE_DRIVER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_personalized_greeted_driver_by_name',
      defaultMessage:
        "Personalized contact by using the driver's name. Greet the driver by using their name",
    }),
    [BehaviorObject.NONROBOTIC_CONTACT]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_personalized_empathetic_nonrobotic',
      defaultMessage: 'Personalized, Empathetic and Non-Robotic contact',
    }),
    [BehaviorObject.PARAPHRASE_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_accurately_paraphrased_issue',
      defaultMessage:
        "Provided a complete and accurate paraphrase of the customer's issue",
    }),
    [BehaviorObject.PARAPHRASE_DRIVER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_accurately_paraphrased_driver_issue',
      defaultMessage:
        "Provided a complete and accurate paraphrase of the driver's issue",
    }),
    [BehaviorObject.EMOTION_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_matched_emotional_state',
      defaultMessage:
        "Aligned conversational style to the customer's emotional state",
    }),
    [BehaviorObject.SAVVINESS_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_matched_tech_savviness',
      defaultMessage: 'Aligned to tech savviness of the customer',
    }),
    [BehaviorObject.EXPERIENCE_DRIVER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_guidance_matched_driver_experience',
      defaultMessage:
        'Adjusted guidance provided based on the experience of the driver',
    }),
    [BehaviorObject.TREATMENT_CUSTOMER]: intl.formatMessage({
      id:
        // eslint-disable-next-line max-len
        'cs_perfportal_coaching_detail_options_behaviour_correct_treatment_for_customer_per_research_and_policy',
      defaultMessage:
        'Applied the correct treatment to customer based on research and policy',
    }),
    [BehaviorObject.CLARIFY_NOTSPECULATE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_clarified_with_questions',
      defaultMessage:
        'Asked clarifying questions about the issue without speculating',
    }),
    [BehaviorObject.CLARIFY_ROOTCAUSE]: intl.formatMessage({
      id:
        // eslint-disable-next-line max-len
        'cs_perfportal_coaching_detail_options_behaviour_asked_relevant_probing_clarifying_questions_for_root_cause',
      defaultMessage:
        'Asked relevant probing and clarifying questions to determine the root cause of the issue',
    }),
    [BehaviorObject.ACCURACY_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_accurate_policy_answered_questions',
      defaultMessage:
        "Accurately quoted policy and answered customer's questions",
    }),
    [BehaviorObject.SELFSERVICE_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_advised_self_service',
      defaultMessage:
        'Advised customer on self-service options and avoiding similar problems in the future',
    }),
    [BehaviorObject.SELFSERVICE_DRIVER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_advised_driver_selfservice_proactive_for_success',
      defaultMessage:
        // eslint-disable-next-line max-len
        'Advised and encouraged driver on self-service options and proactive actions they can take to improve delivery success',
    }),
    [BehaviorObject.IDENTIFY_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_identified_issue_correctly',
      defaultMessage: "Identified customer's issue correctly",
    }),
    [BehaviorObject.RESOLVE_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_resolved_issue',
      defaultMessage: "Resolved the customer's issue",
    }),
    [BehaviorObject.RESOLVE_DRIVER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_resolved_driver_issue',
      defaultMessage: "Resolved the driver's issue",
    }),
    [BehaviorObject.OPTIONS_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_provided_options',
      defaultMessage: 'Provided customer with options',
    }),
    [BehaviorObject.SUMMARY_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_summarized',
      defaultMessage:
        'Summarized actions taken for customer and gave the customer clear next steps and timeline',
    }),
    [BehaviorObject.HELP_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_asked_if_more_help_needed',
      defaultMessage: 'Asked if the customer needed additional help',
    }),
    [BehaviorObject.FAREWELL_CUSTOMER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_feedback_and_farewell',
      defaultMessage:
        'Acknowledged customer feedback and used an appropriate farewell',
    }),
    [BehaviorObject.ESCALATE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_escalated_systemic_problems',
      defaultMessage: 'Escalated systemic problems',
    }),
    [BehaviorObject.SECURITY_VERIFICATION]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_verified_security',
      defaultMessage: 'Complied with all security verification procedures',
    }),
    [BehaviorObject.SENSITIVE_INFORMATION]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_complied_with_customer_and_company_sensitive_info',
      defaultMessage:
        'Complied with all customer and company sensitive information',
    }),
    [BehaviorObject.LANGUAGE_CUSTOMER]: intl.formatMessage({
      id:
        // eslint-disable-next-line max-len
        'cs_perfportal_coaching_detail_options_behaviour_language_implies_customer_innocence_factual_researched',
      defaultMessage:
        'Used language that implies customer innocence and is supported by facts and research',
    }),
    [BehaviorObject.PROFESSIONAL_TONE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_tone_professional_optimistic_helpful_courteous',
      defaultMessage:
        'Used professional tone that is optimistic, helpful, and courteous',
    }),
    [BehaviorObject.LANGUAGE_PROFICIENCY]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_language_proficiency',
      defaultMessage: 'Language proficiency',
    }),
    [BehaviorObject.FOLLOWED_SOP]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_followed_steps_completed_actions_sop_guidance',
      defaultMessage:
        'Followed all steps and completed all actions per SOP guidance',
    }),
    [BehaviorObject.SOCIAL_ASSISTANCE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_followed_social_order_of_assistance',
      defaultMessage: 'Followed the Social order of Assistance',
    }),
    [BehaviorObject.COMPLETE_FOLLOWUP]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_completed_all_followup_actions',
      defaultMessage: 'Completed all follow-up actions',
    }),
    [BehaviorObject.SIC_TAGS]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_selected_correct_sic_used_proper_tags',
      defaultMessage: 'Selected correct SIC codes/ Used proper tags',
    }),
    [BehaviorObject.CONTROLLED_CALL]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_controlled_call_pacing_discussion_direction',
      defaultMessage:
        'Controlled call, including pacing, discussion, and direction',
    }),
    [BehaviorObject.PR_REQUIREMENTS]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_complied_with_pr_requirements',
      defaultMessage: 'Complied with PR requirements',
    }),
    [BehaviorObject.OTHER]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_other',
      defaultMessage: 'Other',
    }),
    [BehaviorObject.CLARIFYING_QUESTIONS]: intl.formatMessage({
      id: 'cs_perfportal_coaching_detail_options_behaviour_asked_clarifying_questions_about_issue',
      defaultMessage: 'Asked clarifying questions about the issue',
    }),
  };

  return getObjectOptions(BehaviorObject, translations);
};

export const getBehaviors = (
  intl: IntlShape,
  verticals?: VerticalType[]
): Option[] =>
  getAllBehaviors(intl).filter(
    option =>
      !verticals ||
      verticals.length === 0 ||
      getValuesByAttribute(behaviorKeysByVertical, verticals).includes(
        option.value
      )
  );
